import { Box, CssBaseline } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
// Renders above I18nProvider (it is the gate's fallback), so it initialises i18n itself.
import "@/i18n/i18n";
import { ThemePreferenceProvider } from "@/theme/ThemePreferenceProvider";
import { MOTION } from "@/theme/tokens";
import { PulseDot } from "@/ui/Animations/PulseDot";
import { SigninLogo } from "../SigninLogo/SigninLogo";

// It is the Suspense fallback the rest of the app waits behind while a language downloads, so it can't
// suspend for one itself. DEFAULT: until that language arrives its label reads in English.
const WITHOUT_SUSPENDING = { useSuspense: false };

/**
 * What every load shows while the maintenance notice is read (usually tens of ms, at most a few seconds). The theme's
 * background paints at once — so the page never flashes white or the raw body colour — and the logo with a quiet
 * pulse appears only once the wait passes `MOTION.DELAY.LOADER_REVEAL`, so a fast load shows no loader at all.
 */
export const MaintenanceLoadingScreen: FC = () => {
	const { t } = useTranslation(undefined, WITHOUT_SUSPENDING);
	const [revealed, setRevealed] = useState(false);

	useEffect(() => {
		const timer = window.setTimeout(() => setRevealed(true), MOTION.DELAY.LOADER_REVEAL);
		return () => window.clearTimeout(timer);
	}, []);

	return (
		<ThemePreferenceProvider>
			<CssBaseline />
			{revealed && (
				<Box
					component="main"
					role="progressbar"
					aria-label={t("Loading")}
					data-testid="maintenance-loading-screen"
					sx={{
						minHeight: "100dvh",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 3,
						"@keyframes maintenanceLoaderEnter": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: `maintenanceLoaderEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: "none" },
					}}
				>
					<SigninLogo />
					<PulseDot />
				</Box>
			)}
		</ThemePreferenceProvider>
	);
};
